import { SignUpCredentials } from '../types/auth';
import { apiUrls } from '../utils/apiUtils';
import { axiosInstance, turnstileTokenManager } from '../utils/fetcher';
import { isEmail, isPhone } from '../utils/validation';
import { notifySignUp } from '../utils/webViewBridge';

/**
 * Service for user authentication and profile operations
 */
export const userService = {
  /**
   * Request account deletion for the current user
   * @param userId The ID of the user to delete
   * @returns Promise resolving when deletion request is complete
   */
  deleteAccount: async (userId: string): Promise<void> => {
    await axiosInstance.delete(apiUrls.deleteAccount(), {
      data: { userId }
    });
    // Clear authentication data after account deletion
    localStorage.removeItem('token');
    localStorage.removeItem('email');
  },

  /**
   * Check if an account already exists with the given email or phone number
   * @param data Object containing either email or phoneNumber
   * @returns Promise resolving to boolean indicating if account exists
   */
  checkAccountExists: async (data: { email?: string; phoneNumber?: string }): Promise<boolean> => {
    const { data: response } = await axiosInstance.post<{ data: { exists: boolean } }>(
      apiUrls.checkAccount(),
      data
    );
    return response.data.exists;
  },

  /**
   * Send OTP code for signup verification
   * @param username Email or phoneNumber number to send OTP to
   * @param turnstileToken Optional Cloudflare Turnstile verification token
   * @returns Promise resolving to boolean indicating success
   */
  sendOtp: async (username: string, turnstileToken?: string): Promise<boolean> => {
    if (!isEmail(username) && !isPhone(username)) {
      throw new Error('Vui lòng nhập Email hoặc Số điện thoại của bạn.');
    }

    // Store Turnstile token in session storage for the API request
    if (turnstileToken) {
      turnstileTokenManager.set(turnstileToken);
    }

    // Build payload without including the token
    const payload = isEmail(username) 
      ? { email: username } 
      : { phoneNumber: username };
      
    try {
      const { data } = await axiosInstance.post(apiUrls.sendSignupOtp(), payload);
      return data === true;
    } finally {
      // Clear token after request completes (success or error)
      turnstileTokenManager.clear();
    }
  },

  /**
   * Verify OTP code sent during signup
   * @param payload Object containing OTP and either email or phoneNumber number
   * @param turnstileToken Optional Cloudflare Turnstile verification token
   * @returns Promise resolving to OTP verification response
   */
  verifyOtp: async (payload: { otp: string, email?: string, phoneNumber?: string }, turnstileToken?: string): Promise<OtpVerificationResponse> => {
    if (!payload.otp || (!payload.email && !payload.phoneNumber)) {
      throw new Error('Vui lòng nhập OTP.');
    }

    // Store Turnstile token in session storage for the API request
    if (turnstileToken) {
      turnstileTokenManager.set(turnstileToken);
    }

    try {
      const { data } = await axiosInstance.post<{ data: OtpVerificationResponse }>(
        apiUrls.verifySignupOtp(),
        payload
      );

      return data.data;
    } finally {
      // Clear token after request completes
      turnstileTokenManager.clear();
    }
  },

  /**
   * Register a new user account
   * @param credentials User signup credentials
   * @returns Promise resolving to signup response including token
   */
  signup: async (credentials: SignUpCredentials) => {
    const { data } = await axiosInstance.post(apiUrls.signup(), credentials);
    // Handle nested API response: { data: { token, email, ... } }
    const responseData = data.data || data;
    localStorage.setItem('token', responseData.token);
    localStorage.setItem('email', responseData.email || '');
    if (responseData.email && responseData.token) {
      // Notify React Native WebView about successful signup
      notifySignUp(responseData.email, responseData.token);
    }

    return responseData;
  },

  /**
   * Get the currently authenticated user's profile
   * @returns Promise resolving to User object
   */
  getCurrentUser: async (): Promise<User> => {
    const { data } = await axiosInstance.get(apiUrls.userProfile());
    return data;
  },

  /**
   * Log out the current user
   * @returns Promise resolving when logout is complete
   */
  logout: async (): Promise<void> => {
    await axiosInstance.post(apiUrls.logout());
    localStorage.removeItem('token');
  },

  /**
   * Send OTP for password reset
   * @param data Object containing either email or phoneNumber
   * @param turnstileToken Optional Cloudflare Turnstile verification token
   */
  sendOtpResetPassword: async (data: { email?: string, phoneNumber?: string }, turnstileToken?: string): Promise<void> => {
    // Store Turnstile token in session storage for the API request
    if (turnstileToken) {
      turnstileTokenManager.set(turnstileToken);
    }
    
    try {
      await axiosInstance.post(apiUrls.sendPasswordResetOtp(), data);
    } finally {
      // Clear token after request completes
      turnstileTokenManager.clear();
    }
  },

  /**
   * Verify OTP sent for password reset
   * @param payload Object containing OTP and either email or phoneNumber
   */
  verifyOtpResetPassword: async (payload: { otp: string, email?: string, phoneNumber?: string }) => {
    return await axiosInstance.post(apiUrls.verifyPasswordResetOtp(), payload);
  },

  /**
   * Reset user password with token from OTP verification
   * @param data Object containing new password and token
   */
  resetPassword: async (data: { phoneNumber?: string, email?: string, password: string, token: string }) => {
    return await axiosInstance.post(apiUrls.resetPassword(), data);
  },

  /**
   * Change the current user's password
   * @param data Object containing current password and new password
   * @returns Promise resolving when password change is complete
   */
  changePassword: async (data: { currentPassword: string, newPassword: string }): Promise<void> => {
    await axiosInstance.post(apiUrls.changePassword(), data);
  },

  /**
   * Update user avatar/profile picture
   * @param file Image file to upload as avatar
   * @returns Promise resolving to the new avatar URL
   */
  updateAvatar: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('avatar', file);

    const { data } = await axiosInstance.post<{ success: boolean; avatarUrl: string }>(
      apiUrls.updateAvatar(),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return data.avatarUrl;
  },

  /**
   * Update user nickname
   * @param nickname The new nickname for the user
   * @returns Promise resolving when nickname update is complete
   */
  updateNickname: async (nickname: string): Promise<void> => {
    await axiosInstance.post(apiUrls.updateNickname(), { nickname });
  },

  /**
   * Update user profile information
   * @param userInfo Object containing user information to update
   * @returns Promise resolving to updated User object
   */
  updateUserInfo: async (userInfo: {
    nickname?: string;
    email?: string;
    phoneNumber?: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    [key: string]: any;
  }): Promise<User> => {
    const { data } = await axiosInstance.put(apiUrls.updateUserProfile(), userInfo);
    return data;
  }
};